import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { HiTrash } from "react-icons/hi";
import ProjectInfoEditBanner from "../adminView/components/ProjectEditBanner/ProjectEditBanner";
import MainImageCard from "../adminView/components/MainImageCard/MainImageCard";
import EditInfoForm from "../adminView/components/EditInfoForm/EditInfoForm";
import OpenApplicationsCard from "../adminView/components/OpenApplicationCard/OpenApplicationCard";
import ApplicantsCard from "../adminView/components/ApplicantsCard/ApplicantsCard";
import TeamMembersCard from "../adminView/components/TeamMembersCard/TeamMembersCard";
import ActiveNotificationBanner from "../../notifications/components/activeNotificationBanner/activeNotificationBanner";
import validateOneLiner from "../../../components/codiOS/utils/validateOneLiner";
import validateProjectDescription from "../../../components/codiOS/utils/validateProjectDescription";
import { validateWithCodi } from "../../../components/codiOS/utils/validateWithCodi";
import Roadmap from "../adminView/components/Roadmap/Roadmap";
import ProjectCalendar from "../adminView/components/ProjectCalendar/ProjectCalendar";
import CodiOS from "../../../components/codiOS/codiOS";
import { computePhaseProgress } from "../adminView/utils/computePhaseProgress";

import "./teamView.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeamView = ({ collapsed, setCollapsed }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [project, setProject] = useState(null);
  const [roadmaps, setRoadmaps] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [triggerRoleRefresh, setTriggerRoleRefresh] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [openRoles, setOpenRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedProject, setUpdatedProject] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeNoti, setActiveNoti] = useState(null);
  const [toastAlerts, setToastAlerts] = useState(true);
  const [showCodiOS, setShowCodiOS] = useState(false);
  const [codiLaunchMode, setCodiLaunchMode] = useState(null);
  const token = localStorage.getItem("token");

  // Unified validation and data loading
  useEffect(() => {
    const validateAndLoad = async () => {
      try {
        const token = localStorage.getItem("token");
        const userEmail = localStorage.getItem("userEmail");
        if (!token || !userEmail) {
          navigate("/login");
          return;
        }

        // 1. Fetch project details
        const projectRes = await fetch(`${BACKEND_URL}/projects/${projectId}/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const projectData = await projectRes.json();
        if (!projectRes.ok) throw new Error("Project not found or access denied.");

        const projectInfo = projectData.project || {};
        // Convert Firestore timestamp to Date
        if (projectInfo.updatedAt?._seconds) {
          projectInfo.updatedAt = new Date(projectInfo.updatedAt._seconds * 1000);
        } else if (typeof projectInfo.updatedAt === "string" || typeof projectInfo.updatedAt === "number") {
          projectInfo.updatedAt = new Date(projectInfo.updatedAt);
        } else {
          projectInfo.updatedAt = null;
        }

        setProject(projectInfo);
        setUpdatedProject({
          title: projectInfo.title || "",
          oneLiner: projectInfo.oneLiner || "",
          description: projectInfo.description || "",
          updatedAt: projectInfo.updatedAt,
        });

        // 2. Fetch team members
        const teamRes = await fetch(
          `${BACKEND_URL}/projects/${projectId}/team-members-admin`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const teamData = await teamRes.json();
        if (!teamRes.ok) throw new Error("Failed to fetch team members.");
        const members = teamData.members || [];
        setTeamMembers(members);

        // Check ownership and membership 
        const isMember = members.some(
          (m) => m.email?.toLowerCase() === userEmail.toLowerCase()
        );
        const isOwner =
          projectData.project?.owner?.toLowerCase() === userEmail.toLowerCase();

        // Set role
        setIsAdmin(isOwner);

        if (!isMember && !isOwner) {
          alert("You are not a member of this project team.");
          navigate(`/projects/${projectId}/details`);
          return;
        }

        // 3. Fetch open roles
        const rolesRes = await fetch(
          `${BACKEND_URL}/applications/${projectId}/open-roles-admin`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (rolesRes.ok) {
          const data = await rolesRes.json();
          setOpenRoles(data.openRoles || []);
        }
      } catch (err) {
        console.error("❌ Error loading data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    validateAndLoad();
  }, [projectId, navigate]);

  // 🛰️ Fetch Roadmaps + Assignments + Pickups + Submissions
  useEffect(() => {
    const fetchRoadmapsAndAssignmentsAndPickups = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        console.log("🚀 TeamView → Fetching Roadmaps + Assignments + Pickups + Submissions for:", projectId);

        const [roadmapRes, assignedRes, pickupsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/roadmaps/${projectId}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetch(`${BACKEND_URL}/roadmaps/${projectId}/assigned-members`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetch(`${BACKEND_URL}/roadmaps/${projectId}/pickups`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
        ]);

        const roadmapData = await roadmapRes.json();
        const assignedData = await assignedRes.json();
        const pickupsData = await pickupsRes.json();

        if (!roadmapData?.roadmaps?.length) {
          console.warn("❌ No roadmaps found.");
          setRoadmaps([]); 
          setSubmissions([]);
          return;
        }

        // --- roadmapId for submissions ---
        const roadmapId = roadmapData.roadmaps[0].id;
        const subsRes = await fetch(`${BACKEND_URL}/submissions/${roadmapId}`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const subsData = await subsRes.json();

        console.log("📬 Submissions Data (TeamView):", subsData);

        // --- assignment / pickup maps ---
        const assignedMap = {};
        (assignedData.assignedCheckpoints || []).forEach((a) => {
          assignedMap[`${a.phase}-${a.index}`] = a.assignedTo;
        });
        const pickupMap = {};
        (pickupsData.pickedUpCheckpoints || []).forEach((p) => {
          pickupMap[`${p.phase}-${p.index}`] = p.pickedUpBy;
        });

        // --- format phases ---
        let formatted = roadmapData.roadmaps.flatMap((rm) =>
          (rm.phases || []).map((phase) => ({
            ...phase,
            roadmapId: rm.id,
            checkpoints: (phase.checkpoints || []).map((cp, i) => ({
              ...cp,
              index: i,
              assignedTo: assignedMap[`${phase.title}-${i}`] || null,
              pickedUpBy: pickupMap[`${phase.title}-${i}`] || null,
            })),
          }))
        );

        // --- merge submission data for progress view ---
        if (subsData?.submissions?.length) {
          formatted = formatted.map((phase) => {
            const updated = phase.checkpoints.map((cp) => {
              const related = subsData.submissions.filter(
                (s) => s.phase === phase.title && s.checkpointIndex === cp.index
              );
              if (!related.length) return cp;
              const avg =
                related.reduce((sum, s) => sum + (s.progressDelta || 0), 0) /
                related.length;
              const last = related[related.length - 1];
              return {
                ...cp,
                progress: {
                  percent: Math.min(100, (avg * 100).toFixed(0)),
                  lastUpdated: last?.createdAt,
                },
              };
            });
            const avgPhase =
              updated.length > 0
                ? Math.round(
                    updated.reduce((s, c) => s + (c.progress?.percent || 0), 0) /
                      updated.length
                  )
                : 0;
            return { ...phase, checkpoints: updated, progress: avgPhase };
          });
        }

        console.log("🎯 Formatted Roadmaps (TeamView):", formatted);
        setRoadmaps(formatted);
        setSubmissions(subsData.submissions || []);
      } catch (err) {
        console.error("🔥 TeamView fetch error:", err);
      }
    };

    fetchRoadmapsAndAssignmentsAndPickups();
  }, [projectId]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  // Validation
  const validateAdminFields = async () => {
    const newErrors = {};
    if (!updatedProject.title?.trim())
      newErrors.title = "Project title is required.";

    const oneLinerCheck = validateOneLiner(updatedProject.oneLiner || "");
    if (!oneLinerCheck.valid) {
      newErrors.oneLiner =
        oneLinerCheck.reason === "too short"
          ? "One-liner is too short."
          : "One-liner is too long.";
    } else {
      const oneLinerMeaning = await validateWithCodi(updatedProject.oneLiner);
      if (!oneLinerMeaning.valid)
        newErrors.oneLiner = oneLinerMeaning.reason || "One-liner lacks clarity.";
    }

    const descCheck = validateProjectDescription(updatedProject.description || "");
    if (!descCheck.valid) {
      newErrors.description =
        descCheck.reason === "too short"
          ? "Description is too short."
          : "Description is too long.";
    } else {
      const descMeaning = await validateWithCodi(updatedProject.description);
      if (!descMeaning.valid)
        newErrors.description = descMeaning.reason || "Description lacks clarity.";
    }

    return newErrors;
  };

  const handleSaveChanges = async () => {
    try {
      const validationErrors = await validateAdminFields();
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        alert("Please fix errors before saving.");
        return;
      }
      const token = localStorage.getItem("token");
      if (!token) return;

      const requestBody = {
        title: updatedProject.title || "",
        oneLiner: updatedProject.oneLiner || "",
        description: updatedProject.description || "",
      };

      const response = await fetch(`${BACKEND_URL}/projects/${projectId}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error("Failed to update project");
      const data = await response.json();
      alert("Project updated successfully!");
      setProject(data.updatedProject);
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update project.");
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`${BACKEND_URL}/projects/${projectId}/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      alert("Project deleted.");
      navigate("/explore");
    } catch (err) {
      console.error(err);
      alert("Error deleting project.");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      await fetch(`${BACKEND_URL}/applications/roles/${roleId}/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setOpenRoles((prev) => prev.filter((r) => r.id !== roleId));
    } catch (err) {
      console.error("Role delete failed:", err);
    }
  };

  // Determine current user from teamMembers list
  const userEmail = localStorage.getItem("userEmail");
  const currentUser =
    teamMembers.find(
      (m) => m.email?.toLowerCase() === userEmail?.toLowerCase()
    ) || { email: userEmail };
  
  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto mt-4 w-full px-2 sm:px-4 lg:px-6">
      {toastAlerts && activeNoti && (
        <ActiveNotificationBanner
          notification={activeNoti}
          onClose={() => setActiveNoti(null)}
        />
      )}

      <div className="flex flex-col gap-6 mx-auto mt-4 w-full max-w-[calc(100%-80px)]">
        <ProjectInfoEditBanner
          project={project}
          onEdit={() => setShowEditModal(true)}
          onDelete={handleDeleteProject}
          // navigateToHub={() => {
          //   const projectIdToUse = project?.id || projectData?.id || projectId;
          //   if (!projectIdToUse) return;
          //   navigate(`/projects/${projectIdToUse}/collaborate`);
          // }}s
          // onImageClick={() => setShowImageModal(true)}
          isAdmin={isAdmin}
        />

        {/* Project Points, Applicants, Team Members Cards */}
        <div className="flex flex-wrap justify-between gap-4 pb-2 px-2">
          {/* First points card */}
          {/* <ProjectPointsCard
            base={capital?.base || 0}
            earned={capital?.earned || 0}
            goal={1000}
            className="flex-1 min-w-[280px] max-w-[320px] transition-transform duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-indigo-200"
          /> */}

          {/* Applicants Section */}
          <div
            className="flex-1 min-w-[280px] max-w-[320px] h-52 bg-white rounded-[20px] p-4 shadow-sm flex flex-col 
                      transition-transform duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-blue-200 animate-fadeIn"
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Applicants</h3>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {openRoles.length > 0 ? (
                openRoles.map((role) => (
                  <ApplicantsCard
                    key={role.id}
                    roleId={role.id}
                    projectId={projectId}
                    roleTitle={role.roleTitle}
                    compact={true}
                  />
                ))
              ) : (
                <p className="text-xs text-gray-500 italic animate-pulse">
                  No applicants yet.
                </p>
              )}
            </div>
          </div>

          {/* Third Team Members card */}
          <TeamMembersCard
            projectId={projectId}
            teamMembers={teamMembers}
            projectOwner={project?.owner}
            className="flex-1 min-w-[280px] max-w-[320px] transition-transform duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-green-200 animate-fadeIn"
          />
        </div>

        <ProjectCalendar
          roadmaps={roadmaps}
          teamMembers={teamMembers}
          submissions={submissions}
          isAdmin={false}
          currentUser={currentUser}
        />

        {/* <OpenApplicationsCard
          onRoleAdded={() => setTriggerRoleRefresh((prev) => prev + 1)}
          projectTitle={project?.title}
        /> */}

        {openRoles.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Open Roles</h3>
            <div className="space-y-4">
              {openRoles.map((role) => (
                <div
                  key={role.id}
                  className="p-4 bg-gray-50 rounded-lg shadow-sm border flex flex-col gap-2 w-full"
                >
                  <div className="flex justify-between items-start w-full">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 break-words">{role.roleTitle}</p>
                      <p className="text-sm text-gray-600 break-words">{role.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created on:{" "}
                        {new Date(role.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2 items-center flex-shrink-0">
                      <label
                        className={`relative inline-flex items-center ${
                          isAdmin ? "cursor-pointer" : "cursor-not-allowed"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={role.status !== "closed"}
                          disabled={!isAdmin} // disables click for non-admins
                          onChange={async (e) => {
                            if (!isAdmin) return; // guard just in case
                            const newStatus = e.target.checked ? "open" : "closed";
                            const token = localStorage.getItem("token");
                            try {
                              const res = await fetch(
                                `${BACKEND_URL}/applications/roles/${role.id}/status`,
                                {
                                  method: "PATCH",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                  },
                                  body: JSON.stringify({ newStatus }),
                                }
                              );
                              if (!res.ok) throw new Error("Failed to update role status");
                              localStorage.setItem(
                                "justToggledRoleStatus",
                                JSON.stringify({ roleTitle: role.roleTitle, status: newStatus })
                              );
                              window.location.reload();
                            } catch (err) {
                              console.error("Error toggling role status:", err);
                              alert("Failed to update role status.");
                            }
                          }}
                        />
                        {/* Switch background */}
                        <div
                          className={`w-11 h-6 rounded-full transition-colors duration-300 ${
                            role.status !== "closed"
                              ? "bg-blue-600"
                              : "bg-gray-300"
                          } ${!isAdmin ? "opacity-50" : ""}`}
                        ></div>

                        {/* Switch knob */}
                        <div
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${
                            role.status !== "closed" ? "translate-x-5" : ""
                          }`}
                        ></div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 shadow-sm w-full">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Roadmap View</h3>
          <Roadmap
            roadmaps={roadmaps}
            submissions={submissions}
            isAdmin={isAdmin}
            onCreateRoadmap={() => {
              const currentProject = project;
              console.log("currentProject:", currentProject);

              if (!currentProject?.id) {
                console.warn("⚠️ Project data not ready — cannot open CodiOS yet");
                return;
              }

              console.log("🚀 Launching CodiOS with:", currentProject);
              setCodiLaunchMode("roadmap");
              setShowCodiOS(true);
            }}
          />

        </div>
      </div>

      {/* Popup with Project Info + Image */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex justify-center items-center">
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-lg relative flex flex-col"
            style={{ maxHeight: "90vh" }} // lock modal height relative to viewport
          >
            {/* Close Button */}
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-6 space-y-6">
              {/* Project Image Upload */}
              <MainImageCard
                project={project}
                onImageUpdate={(file) => {
                  setUpdatedProject((prev) => ({ ...prev, image: file }));
                }}
              />

              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your Project Info</h2>
                  <span className="mt-1 inline-block text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {project?.category || "No Category"}
                  </span>
                </div>
                {/* <div className="flex gap-2 items-center">
                  <button
                    onClick={() => {
                      const projectIdToUse = project?.id || projectData?.id || projectId;
                      if (!projectIdToUse) return;
                      navigate(`/projects/${projectIdToUse}/collaborate`);
                    }}
                    className="flex items-center text-sm text-gray-700 hover:text-black font-medium"
                  >
                    <HiOutlineChatBubbleLeftRight className="mr-1 w-4 h-4" />
                    Hub
                  </button>
                  <button
                    onClick={handleDeleteProject}
                    className="text-red-500 hover:text-red-700"
                    title="Delete Project"
                  >
                    <HiTrash className="w-5 h-5" />
                  </button>
                </div> */}
              </div>

              {/* Project Info Form */}
              <EditInfoForm
                updatedProject={updatedProject}
                setUpdatedProject={setUpdatedProject}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                readOnly={!isAdmin} // controls edit/view mode
                isAdmin={isAdmin}   // shows proper banner
              />

              {/* Tags */}
              <div>
                {project?.tags?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No Tags Available</p>
                )}
              </div>

              {/* Save Button */}
              {isAdmin ? (
                <div>
                  <button
                    onClick={handleSaveChanges}
                    className="w-full bg-black text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-900 transition"
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <div className="w-full bg-gray-100 text-gray-400 py-2.5 rounded-lg text-sm font-medium text-center">
                  View Only Mode
                </div>
              )}

              {/* Last Updated */}
              {project?.updatedAt && (
                <p className="text-xs text-gray-400 text-right">
                  Last updated:{" "}
                  {new Date(project.updatedAt).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {showCodiOS && (
        <CodiOS
          onClose={() => setShowCodiOS(false)}
          initialMode={codiLaunchMode}
          project={{
            id: project.id, 
            title: project.title,
            oneLiner: project.oneLiner,
            description: project.description,
            category: project.category,
            tags: project.tags,
          }}
        />
      )}

    </div>

  
  );
};

export default TeamView;
